"use client"

import { FiArrowUpRight } from "react-icons/fi"
import { ResourceIcon } from "@/components/resources/resource-icon"
import { cn } from "@/lib/utils"
import type { ResourceItem } from "@/types/resources"

interface ResourceCardProps {
    item: ResourceItem
    className?: string
}

export function ResourceCard({ item, className }: ResourceCardProps) {
    const isDaily = item.badge === "daily"

    return (
        <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
                "group relative flex items-start gap-3 border border-transparent p-2.5 transition-all duration-200",
                "hover:border-border/70 hover:bg-muted/40 dark:hover:border-border/40 dark:hover:bg-muted/20",
                className
            )}
        >
            <ResourceIcon
                svglRoute={item.svglRoute}
                iconKey={item.iconKey}
                title={item.title}
                className="mt-0.5"
            />

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-foreground text-sm tracking-tight transition-colors group-hover:text-foreground">
                        {item.title}
                    </span>

                    {isDaily && (
                        <span className="shrink-0 font-mono text-[11px] text-emerald-700 dark:text-emerald-400">
                            [DAILY]
                        </span>
                    )}

                    {item.badge === "essential" && (
                        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                            [CORE]
                        </span>
                    )}

                    {item.badge === "favorite" && (
                        <span className="shrink-0 font-mono text-[11px] text-amber-800 dark:text-amber-400">
                            [FAV]
                        </span>
                    )}

                    <FiArrowUpRight
                        className="ml-auto size-3.5 shrink-0 text-muted-foreground/40 opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground group-hover:opacity-100"
                        aria-hidden="true"
                    />
                </div>

                <p className="mt-1 line-clamp-2 text-muted-foreground text-xs leading-relaxed">
                    {item.description}
                </p>
            </div>
        </a>
    )
}
